import { Button } from "@/components/ui/button";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CreateUnifiedOrderDialog } from "./unified/create-unified-order-dialog";
import { Plus, ChevronDown, Sparkles, Zap } from "lucide-react";

interface CreateOrderButtonProps {
  trigger?: ReactNode;
  variant?: "default" | "dialog";
}

export function CreateOrderButton({
  trigger,
  variant = "default",
}: CreateOrderButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const onSuccess = () => {
    setIsDialogOpen(false);
  };

  const handleFullPageClick = () => {
    void navigate("/fulfillment/orders/create");
  };

  // If custom trigger is provided, use dialog variant
  if (trigger) {
    return (
      <>
        <div onClick={() => setIsDialogOpen(true)}>{trigger}</div>
        <CreateUnifiedOrderDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSuccess={onSuccess}
        />
      </>
    );
  }

  // If variant is dialog, show dialog directly
  if (variant === "dialog") {
    return (
      <>
        <Button variant="default" onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Button>
        <CreateUnifiedOrderDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onSuccess={onSuccess}
        />
      </>
    );
  }

  // Default behavior with dropdown menu
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="default">
            <Plus className="h-4 w-4 mr-2" />
            Create Order
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setIsDialogOpen(true)}>
            <Zap className="h-4 w-4 mr-2" />
            Quick Order (Dialog)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleFullPageClick}>
            <Sparkles className="h-4 w-4 mr-2" />
            Create Order
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CreateUnifiedOrderDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSuccess={onSuccess}
      />
    </>
  );
}
